import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DeviceRegistry from './device/DeviceRegistry';
import PINVerification from './device/PINVerification';
import RecoveryCode from './device/RecoveryCode';

const DeviceLockManagement: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'devices' | 'security'>('devices');
  const [testDeviceId, setTestDeviceId] = useState('');
  const [showPINTest, setShowPINTest] = useState(false);
  const [showRecoveryTest, setShowRecoveryTest] = useState(false);

  return (
    <div className="space-y-6 pt-16 lg:pt-0">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-4xl font-bold text-gradient-cyan mb-2">
            🔒 {t('deviceLock.title')}
          </h2>
          <p className="text-[#B8B8C3]">{t('deviceLock.description')}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#31383D]">
        <button
          onClick={() => setActiveTab('devices')}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeTab === 'devices'
              ? 'text-cyan-400 border-b-2 border-cyan-400'
              : 'text-[#B8B8C3] hover:text-white'
          }`}
        >
          {t('deviceLock.devices')}
        </button>
        <button
          onClick={() => setActiveTab('security')}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeTab === 'security'
              ? 'text-cyan-400 border-b-2 border-cyan-400'
              : 'text-[#B8B8C3] hover:text-white'
          }`}
        >
          {t('deviceLock.security')}
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'devices' && <DeviceRegistry />}
      
      {activeTab === 'security' && (
        <div className="space-y-6">
          <div className="card-dark p-6">
            <h3 className="text-2xl font-bold text-white mb-4">{t('deviceLock.securitySettings')}</h3>
            
            <div className="space-y-4">
              <div className="p-4 bg-[#15191E] rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className="text-lg font-semibold text-white">{t('deviceLock.autolockTimeout')}</h4>
                    <p className="text-sm text-[#B8B8C3]">{t('deviceLock.autolockTimeoutDesc')}</p>
                  </div>
                  <select className="input-dark">
                    <option value="1">1 {t('deviceLock.minute')}</option>
                    <option value="5">5 {t('deviceLock.minutes')}</option>
                    <option value="15">15 {t('deviceLock.minutes')}</option>
                    <option value="30">30 {t('deviceLock.minutes')}</option>
                    <option value="never">{t('deviceLock.never')}</option>
                  </select>
                </div>
              </div>

              <div className="p-4 bg-[#15191E] rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className="text-lg font-semibold text-white">{t('deviceLock.requirePINOnStartup')}</h4>
                    <p className="text-sm text-[#B8B8C3]">{t('deviceLock.requirePINOnStartupDesc')}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500"></div>
                  </label>
                </div>
              </div>

              <div className="p-4 bg-[#15191E] rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className="text-lg font-semibold text-white">{t('deviceLock.remoteWipe')}</h4>
                    <p className="text-sm text-[#B8B8C3]">{t('deviceLock.remoteWipeDesc')}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500"></div>
                  </label>
                </div>
              </div>

              <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <h4 className="text-lg font-semibold text-yellow-400 mb-1">{t('deviceLock.securityWarning')}</h4>
                    <p className="text-sm text-[#B8B8C3]">{t('deviceLock.securityWarningDesc')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card-dark p-6">
            <h3 className="text-2xl font-bold text-white mb-4">{t('deviceLock.testPINVerification')}</h3>
            <p className="text-[#B8B8C3] mb-4">{t('deviceLock.testPINVerificationDesc')}</p>
            
            {!showPINTest && !showRecoveryTest ? (
              <div className="bg-[#15191E] p-4 rounded-lg">
                <p className="text-sm text-[#B8B8C3] mb-2">{t('deviceLock.enterDeviceId')}</p>
                <input
                  type="text"
                  value={testDeviceId}
                  onChange={(e) => setTestDeviceId(e.target.value)}
                  className="input-dark w-full mb-4"
                  placeholder="aa-2001-09"
                />
                <button 
                  onClick={() => {
                    if (testDeviceId.trim()) {
                      setShowPINTest(true);
                    }
                  }}
                  disabled={!testDeviceId.trim()}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('deviceLock.testVerification')}
                </button>
              </div>
            ) : showPINTest ? (
              <PINVerification
                deviceId={testDeviceId}
                onSuccess={() => {
                  alert(t('deviceLock.verificationSuccess'));
                  setShowPINTest(false);
                  setTestDeviceId('');
                }}
                onRecovery={() => {
                  setShowPINTest(false);
                  setShowRecoveryTest(true);
                }}
              />
            ) : (
              <RecoveryCode
                deviceId={testDeviceId}
                onSuccess={() => {
                  alert(t('deviceLock.recoverySuccess'));
                  setShowRecoveryTest(false);
                  setTestDeviceId('');
                }}
                onBack={() => {
                  setShowRecoveryTest(false);
                  setShowPINTest(true);
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceLockManagement;
